/**
 * @file Hypervisor hooks — the admin-gated machine list, its live
 *       freshness subscription, and the two bounded control-plane command
 *       mutations (operator-suspend, force-expire-pin).
 * @module @labcraft/admin/ui/hooks/use-hypervisor
 *
 * Reads `GET /api/hypervisor` through `admin.hypervisor.list()`. The
 * response carries `connected` — `false` until an admin→hypervisor
 * connection is wired, in which case `machines` is empty and the panel
 * renders an honest "not connected yet" surface. Each row carries its live
 * lifecycle `state` (#1337) and engagement `pin` (#1340).
 *
 * Freshness (umbrella #1258 Part H / #1340): `useHypervisorFleetLifecycle`
 * subscribes to the hypervisor's fleet-lifecycle stream and invalidates the
 * machine-list query on every transition frame, so rows + action
 * affordances track reality live. A conservative `refetchInterval`
 * reconcile-poll backs it up in case the WS drops.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * / `useMutation` outside it.
 */

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import type { HypervisorListResponse } from '../../shared/hypervisor_routes.ts'
import { qkHypervisorMachines } from './query_keys.ts'

/**
 * Reconcile-poll fallback interval (ms). The fleet-lifecycle subscription
 * is the primary freshness driver; this slow poll only backstops a dropped
 * WS so the panel can never wedge on a stale list.
 */
const HYPERVISOR_RECONCILE_MS = 15_000

/** Subscribe to the hypervisor machine list. */
export function useHypervisorMachines() {
  const client = useClient<Admin>()
  return useQuery<HypervisorListResponse>({
    queryKey: qkHypervisorMachines,
    queryFn: () => client.hypervisor.list(),
    refetchInterval: HYPERVISOR_RECONCILE_MS,
  })
}

/**
 * Keep the machine list fresh off the hypervisor's fleet-lifecycle stream
 * (umbrella #1258 Part H / #1340). Opens the observer on mount, invalidates
 * the machine-list query on every transition frame (so rows + affordances
 * re-derive from the fresh state), and tears the subscription down on
 * unmount. Returns nothing — it wires a side-effect, not data.
 */
export function useHypervisorFleetLifecycle(): void {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  useEffect(() => {
    const driver = client.hypervisor.subscribeFleetLifecycle()
    const off = driver.onFrame(() => {
      void queryClient.invalidateQueries({ queryKey: qkHypervisorMachines })
    })
    driver.attach()
    return () => {
      off()
      driver.close()
    }
  }, [client, queryClient])
}

/**
 * Operator-suspend a running VM (umbrella #1258 Part H / #1337). On
 * success, invalidates the machine-list query so the row's state pill +
 * affordances re-derive (the fleet subscription would land the same update
 * moments later; the eager invalidation gives immediate feedback).
 */
export function useOperatorSuspendMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => client.hypervisor.operatorSuspend(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qkHypervisorMachines })
    },
  })
}

/**
 * Force-expire a VM's engagement pin (umbrella #1258 Part H / #1340). On
 * success, invalidates the machine-list query so the pin's `stale` flag
 * re-derives on the next read.
 */
export function useForceExpirePinMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => client.hypervisor.forceExpirePin(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qkHypervisorMachines })
    },
  })
}
